/**
 * Point Cloud Eraser Extension
 * Allows users to select and hide points within the cropbox using rectangle selection
 *
 * Implements proper Forge ToolInterface pattern for event handling
 */


const THREE = window.THREE;
const Autodesk = window.Autodesk;

import {
    EraserState,
    getMousePosition,
    createSelectionRect,
    screenRectToFrustum,
    pointerToRay,
    intersectRayWithBox
} from '../utils/pointCloudEraser';
import { pointerToRaycaster } from '../utils/forgeUtils';
import store from '@/store';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

/**
 * PointCloudEraserTool - Handles user input via Forge ToolInterface
 * This tool intercepts mouse events with high priority to prevent default camera controls
 */
class PointCloudEraserTool extends Autodesk.Viewing.ToolInterface {
    constructor(viewer, extension) {
        super();
        this.viewer = viewer;
        this.extension = extension;
        this.names = ['point-cloud-eraser-tool'];

        // Delete instance methods to force tool controller to call class methods
        // (Required pattern for Forge ToolInterface - see WalkingPathExt.js)
        delete this.register;
        delete this.deregister;
        delete this.activate;
        delete this.deactivate;
        delete this.getPriority;
        delete this.handleMouseMove;
        delete this.handleButtonDown;
        delete this.handleButtonUp;
        delete this.handleKeyDown;

        // Selection state
        this.isSelecting = false;
        this.selectionStart = null;
        this.selectionEnd = null;
    }

    /**
     * Returns names this tool is registered under
     */
    getNames() {
        return this.names;
    }

    /**
     * Returns tool name
     */
    getName() {
        return this.names[0];
    }

    /**
     * Tool priority - higher than default tools (0) to intercept events first
     */
    getPriority() {
        return 100;
    }

    /**
     * Called when tool is activated by tool controller
     */
    activate(name) {
        this.isActive = true;

        // Update cursor
        this.extension.updateCursorStyle(true);

        // Show overlay canvas
        this.extension.resizeOverlayCanvas();
    }

    /**
     * Called when tool is deactivated by tool controller
     */
    deactivate(name) {
        this.isActive = false;

        // Cancel any active selection
        if (this.isSelecting) {
            this.cancelSelection();
        }

        // Update cursor
        this.extension.updateCursorStyle(false);

        // Hide overlay
        this.extension.hideSelectionOverlay();
    }

    /**
     * Handle mouse button down
     */
    handleButtonDown(event, button) {
        if (!this.isActive) {
            return false;
        }

        // Only handle left mouse button
        if (button !== 0) {
            return false;
        }

        // Check if cropbox is active
        if (!this.extension.isCropboxActive()) {
            debugWarn('PointCloudEraserExtension', 'Cropbox must be active');
            return false;
        }

        // Lock navigation to prevent camera movement during selection
        this.viewer.navigation.toOrthographic();
        this.viewer.navigation.setIsLocked(true);

        // Start selection
        const mousePos = getMousePosition(event, this.viewer.canvas);
        this.selectionStart = mousePos;
        this.selectionEnd = mousePos;
        this.isSelecting = true;

        this.extension.showSelectionOverlay();
        this.extension.resizeOverlayCanvas();

        // Return true to prevent event from reaching other tools
        return true;
    }

    /**
     * Handle mouse move
     */
    handleMouseMove(event) {
        if (!this.isActive || !this.isSelecting) {
            return false;
        }

        // Update selection end point
        const mousePos = getMousePosition(event, this.viewer.canvas);
        this.selectionEnd = mousePos;

        // Draw 2D selection rectangle
        this.drawSelectionRectangle();

        // Return true to prevent camera controls during drag
        return true;
    }

    /**
     * Handle mouse button up
     */
    handleButtonUp(event, button) {
        if (!this.isActive || !this.isSelecting) {
            return false;
        }

        // Only handle left mouse button
        if (button !== 0) {
            return false;
        }

        // Unlock navigation
        this.viewer.navigation.setIsLocked(false);

        // Finish selection
        const mousePos = getMousePosition(event, this.viewer.canvas);
        this.selectionEnd = mousePos;

        // Execute eraser action
        this.erasePointsInSelection();

        // Hide 2D overlay
        this.extension.hideSelectionOverlay();

        // Reset selection state
        this.isSelecting = false;
        this.selectionStart = null;
        this.selectionEnd = null;

        // Return true to prevent event from reaching other tools
        return true;
    }

    /**
     * Handle keyboard events
     */
    handleKeyDown(event, keyCode) {
        if (!this.isActive) {
            return false;
        }

        // ESC to cancel selection
        if (keyCode === 27 && this.isSelecting) {
            this.cancelSelection();
            return true;
        }

        // Ctrl+Z to undo
        if (event.ctrlKey && keyCode === 90) {
            this.extension.undo();
            return true;
        }

        // Ctrl+Y to redo
        if (event.ctrlKey && keyCode === 89) {
            this.extension.redo();
            return true;
        }

        return false;
    }

    /**
     * Cancel current selection
     */
    cancelSelection() {
        if (this.isSelecting) {
            this.viewer.navigation.toPerspective();
            this.viewer.navigation.setIsLocked(false);
        }

        this.isSelecting = false;
        this.extension.hideSelectionOverlay();
        this.selectionStart = null;
        this.selectionEnd = null;
    }

    /**
     * Draw selection rectangle on overlay
     */
    drawSelectionRectangle() {
        const overlayCanvas = this.extension.overlayCanvas;
        if (!overlayCanvas || !this.selectionStart || !this.selectionEnd) {
            return;
        }

        const ctx = overlayCanvas.getContext('2d');
        const rect = createSelectionRect(this.selectionStart, this.selectionEnd);

        // Clear previous drawing
        ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);

        // Draw rectangle
        ctx.strokeStyle = '#ff0000';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(rect.minX, rect.minY, rect.width, rect.height);

        // Fill with semi-transparent red
        ctx.fillStyle = 'rgba(255, 0, 0, 0.1)';
        ctx.fillRect(rect.minX, rect.minY, rect.width, rect.height);
    }


    /**
     * Erase points within the current selection rectangle
     */
    erasePointsInSelection() {
        if (!this.selectionStart || !this.selectionEnd) {
            return;
        }

        const rect = createSelectionRect(this.selectionStart, this.selectionEnd);

        // Minimum selection size (avoid accidental tiny selections)
        if (rect.width < 5 || rect.height < 5) {
            return;
        }

        // Get cropbox
        const cropBox = this.extension.getCropBox();
        if (!cropBox) {
            debugWarn('PointCloudEraserExtension', 'No active cropbox');
            return;
        }

        // Convert screen rectangle to 3D frustum (ray-box intersection)
        const frustum = screenRectToFrustum(rect, this.viewer, cropBox);

        // Create bounding box from frustum corners
        const erasedVolume = new THREE.Box3();
        [...frustum.cornersNear, ...frustum.cornersFar].forEach(corner => {
            erasedVolume.expandByPoint(corner);
        });

        if (erasedVolume.isEmpty()) {
            debugWarn('PointCloudEraserExtension', 'No volume to erase - rays missed cropbox');
            return;
        }

        // Save current state for undo
        this.extension.eraserState.pushToHistory();

        // Add volume to state
        if (this.extension.eraserState.addVolume(erasedVolume)) {
            // Update shader uniforms
            this.extension.updateShaderUniforms();

            // Dispatch event
            this.viewer.dispatchEvent({
                type: 'points-erased',
                volumeCount: this.extension.eraserState.getTotalHiddenCount()
            });

            // Trigger render update
            this.viewer.impl.invalidate(true);
        } else {
            debugWarn('PointCloudEraserExtension', 'Failed to add volume - max limit reached');
        }
    }
}

/**
 * PointCloudEraserExtension - Main extension class
 * Manages the eraser tool lifecycle and provides utility methods
 */
export default class PointCloudEraserExtension extends Autodesk.Viewing.Extension {
    constructor(viewer, options) {
        super(viewer, options);
        this.viewer = viewer;
        this.options = options || {};

        // Eraser state
        this.eraserState = new EraserState();

        // Visual elements
        this.overlayCanvas = null;

        // Tool instance
        this.tool = null;

        // Reference to main extension for point cloud access
        this.mainExtension = null;
        this.cropping3DExtension = null;
    }

    load() {

        // Create selection overlay
        this.createSelectionOverlay();

        // Get references to other extensions
        this.getExtensionReferences();

        // Create tool instance
        this.tool = new PointCloudEraserTool(this.viewer, this);

        // Register tool with viewer's tool controller
        this.viewer.toolController.registerTool(this.tool);

        return true;
    }

    unload() {

        // Deactivate if active
        this.deactivate();

        // Deregister tool
        if (this.tool) {
            this.viewer.toolController.deregisterTool(this.tool);
            this.tool = null;
        }

        // Remove overlay
        this.removeSelectionOverlay();

        return true;
    }

    /**
     * Get references to required extensions
     */
    getExtensionReferences() {
        // Wait for main extension to be available
        const checkExtensions = () => {
            this.mainExtension = store.state.forgeService.getMainExtension();
            this.cropping3DExtension = store.state.forgeService.getCropping3DExtension();

            if (!this.mainExtension) {
                setTimeout(checkExtensions, 100);
            }
        };

        checkExtensions();
    }

    /**
     * Activate eraser mode
     */
    activate() {
        // Check if cropbox is active
        if (!this.isCropboxActive()) {
            debugWarn('PointCloudEraserExtension', 'Cropbox must be active to use eraser tool');
            this.viewer.dispatchEvent({
                type: 'eraser-activation-failed',
                message: 'Please activate cropbox first'
            });
            return false;
        }

        // Activate tool through tool controller
        const activated = this.viewer.toolController.activateTool(this.tool.getName());

        if (activated) {
            this.viewer.dispatchEvent({
                type: 'eraser-activated'
            });
        }

        return activated;
    }

    /**
     * Deactivate eraser mode
     */
    deactivate() {
        // Deactivate tool through tool controller
        const deactivated = this.viewer.toolController.deactivateTool(this.tool.getName());

        if (deactivated) {
            this.viewer.dispatchEvent({
                type: 'eraser-deactivated'
            });
        }
    }

    /**
     * Toggle eraser mode on/off
     */
    toggle() {
        const isActive = this.viewer.toolController.isToolActivated(this.tool.getName());

        if (isActive) {
            this.deactivate();
            this.viewer.navigation.toPerspective();
        } else {
            this.activate();
            this.viewer.navigation.toOrthographic();
        }
    }

    /**
     * Check if cropbox is currently active
     */
    isCropboxActive() {
        if (!this.cropping3DExtension) {
            this.cropping3DExtension = store.state.forgeService.getCropping3DExtension();
        }

        // Check if there's an active cropbox
        return this.cropping3DExtension &&
               this.cropping3DExtension.cropBox !== null &&
               this.cropping3DExtension.cropBox !== undefined;
    }

    /**
     * Get current cropbox
     */
    getCropBox() {
        if (!this.cropping3DExtension) return null;
        return this.cropping3DExtension.cropBox || null;
    }

    /**
     * Get visible point cloud nodes
     */
    getVisibleNodes() {
        if (!this.mainExtension || !this.mainExtension._pointclouds) {
            return [];
        }

        const nodes = [];
        this.mainExtension._pointclouds.forEach(pointcloud => {
            if (pointcloud.visibleNodes) {
                nodes.push(...pointcloud.visibleNodes);
            }
        });

        return nodes;
    }

    /**
     * Get point cloud objects (for accessing pcoGeometry offset)
     */
    getPointClouds() {
        if (!this.mainExtension || !this.mainExtension._pointclouds) {
            return [];
        }
        return this.mainExtension._pointclouds;
    }

    /**
     * Create selection overlay canvas
     */
    createSelectionOverlay() {
        this.overlayCanvas = document.createElement('canvas');
        this.overlayCanvas.style.position = 'absolute';
        this.overlayCanvas.style.top = '0';
        this.overlayCanvas.style.left = '0';
        this.overlayCanvas.style.pointerEvents = 'none';
        this.overlayCanvas.style.zIndex = '1000';
        this.overlayCanvas.style.display = 'none';

        const container = this.viewer.container;
        container.appendChild(this.overlayCanvas);

        this.resizeOverlayCanvas();
    }

    /**
     * Remove selection overlay
     */
    removeSelectionOverlay() {
        if (this.overlayCanvas && this.overlayCanvas.parentNode) {
            this.overlayCanvas.parentNode.removeChild(this.overlayCanvas);
        }
        this.overlayCanvas = null;
    }

    /**
     * Resize overlay canvas to match viewer
     */
    resizeOverlayCanvas() {
        if (!this.overlayCanvas) return;

        const rect = this.viewer.container.getBoundingClientRect();
        this.overlayCanvas.width = rect.width;
        this.overlayCanvas.height = rect.height;
        this.overlayCanvas.style.width = rect.width + 'px';
        this.overlayCanvas.style.height = rect.height + 'px';
    }

    /**
     * Show selection overlay
     */
    showSelectionOverlay() {
        if (this.overlayCanvas) {
            this.overlayCanvas.style.display = 'block';
        }
    }

    /**
     * Hide selection overlay
     */
    hideSelectionOverlay() {
        if (this.overlayCanvas) {
            this.overlayCanvas.style.display = 'none';
            const ctx = this.overlayCanvas.getContext('2d');
            ctx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
        }
    }

    /**
     * Update shader uniforms with current erased volumes (shader-based approach)
     */
    updateShaderUniforms() {
        if (!this.mainExtension || !this.mainExtension._pointclouds) {
            debugWarn('PointCloudEraserExtension', 'No point clouds available for shader update');
            return;
        }

        const matrices = this.eraserState.getVolumeMatrices();

        // Update material uniforms for all point clouds using setErasedVolumes
        this.mainExtension._pointclouds.forEach(pointcloud => {
            if (pointcloud.material && pointcloud.material.setErasedVolumes) {
                pointcloud.material.setErasedVolumes(matrices);
            }
        });
    }

    /**
     * Undo last eraser action
     */
    undo() {
        if (this.eraserState.undo()) {
            // Update shader uniforms
            this.updateShaderUniforms();

            this.viewer.dispatchEvent({
                type: 'eraser-undo',
                volumeCount: this.eraserState.getTotalHiddenCount()
            });

            this.viewer.impl.invalidate(true);
        }
    }

    /**
     * Redo last undone action
     */
    redo() {
        if (this.eraserState.redo()) {
            // Update shader uniforms
            this.updateShaderUniforms();

            this.viewer.dispatchEvent({
                type: 'eraser-redo',
                volumeCount: this.eraserState.getTotalHiddenCount()
            });

            this.viewer.impl.invalidate(true);
        }
    }

    /**
     * Clear all erased volumes
     */
    clearAll() {
        if (this.eraserState.getTotalHiddenCount() === 0) {
            return;
        }

        // Save state for undo
        this.eraserState.pushToHistory();

        // Clear all volumes
        this.eraserState.clearAll();

        // Update shader uniforms (empty array)
        this.updateShaderUniforms();

        this.viewer.dispatchEvent({
            type: 'eraser-cleared'
        });

        this.viewer.impl.invalidate(true);
    }

    /**
     * Update cursor style
     */
    updateCursorStyle(active) {
        const canvas = this.viewer.canvas;
        if (!canvas) return;

        if (active) {
            canvas.style.cursor = 'crosshair';
        } else {
            canvas.style.cursor = 'default';
        }
    }

    /**
     * Get current state statistics
     */
    getStats() {
        const isActive = this.tool ? this.viewer.toolController.isToolActivated(this.tool.getName()) : false;

        return {
            active: isActive,
            totalHidden: this.eraserState.getTotalHiddenCount(),
            canUndo: this.eraserState.undoStack.length > 0,
            canRedo: this.eraserState.redoStack.length > 0
        };
    }

    /**
     * Export eraser state
     */
    exportState() {
        return this.eraserState.exportState();
    }

    /**
     * Import eraser state
     */
    importState(state) {
        this.eraserState.importState(state);

        // Update shader uniforms
        this.updateShaderUniforms();

        this.viewer.impl.invalidate(true);
    }
}

// Register extension
Autodesk.Viewing.theExtensionManager.registerExtension(
    'PointCloudEraserExtension',
    PointCloudEraserExtension
);
